import axios from "axios";

import { STRIPE_PROXY_URL, withRetries } from "./recnetApi";

/**
 * Operaciones de facturación de Stripe.
 *
 * Todas pasan por `STRIPE_PROXY_URL` (ver `recnetApi.js`): la API de AWS no
 * admite POST desde el navegador por CORS, y el proxy sí. Son las mismas rutas
 * y cuerpos que usa la app móvil (`recnet-24/app/screens/PaymentMethod`): si
 * aquí se enviara otra cosa, el mismo cliente acabaría con estados distintos
 * según desde dónde gestione su membresía.
 *
 * Sólo se reintentan las lecturas. Las operaciones que cobran, cancelan o
 * borran se envían una vez: reintentar a ciegas un cobro puede duplicarlo.
 */

/** Cliente de Stripe por correo, o null si no existe. */
async function clientePorCorreo(email) {
  const { data } = await withRetries(() =>
    axios.get(`${STRIPE_PROXY_URL}/customer/${encodeURIComponent(email)}`)
  );
  return (data && data.customer && data.customer.data && data.customer.data[0]) || null;
}

/**
 * Sólo busca. Se usa al mostrar la pantalla: consultar tu membresía no debe
 * crear nada en Stripe como efecto secundario.
 */
export function buscarCliente(perfil) {
  const email = perfil && perfil.email;
  if (!email) return Promise.resolve(null);
  return clientePorCorreo(email);
}

/**
 * Devuelve el cliente de Stripe del usuario, creándolo si no existe. Sólo
 * debe llamarse cuando el usuario va a registrar una tarjeta.
 *
 * El POST de creación no devuelve el cliente en un formato estable, así que
 * hay que releerlo — mismo tratamiento que hace la app.
 */
export async function asegurarCliente(perfil) {
  const email = perfil && perfil.email;
  if (!email) throw new Error("No hay correo para identificar al cliente.");

  const existente = await clientePorCorreo(email);
  if (existente) return existente;

  await axios.post(`${STRIPE_PROXY_URL}/customer`, {
    name: perfil.name || "",
    phone: perfil.phone || "",
    email,
    description: perfil.tipo === "comercio" ? "comercio" : "user",
  });

  return clientePorCorreo(email);
}

/**
 * True si el fallo es un bloqueo del navegador (red/CORS): axios lo reporta sin
 * `response`. Con el proxy ya no debería ocurrir, pero se conserva para
 * distinguir una caída de red de un error real del backend.
 */
export function esBloqueoDelNavegador(error) {
  return !!error && !error.response;
}

/** SetupIntent para guardar una tarjeta sin cobrarla. */
export async function crearIntentDeTarjeta(clientId) {
  const { data } = await axios.post(`${STRIPE_PROXY_URL}/payment_method/intent`, {
    client_id: clientId,
  });
  return data && data.clientSecret;
}

/** Tarjetas guardadas del cliente. */
export async function listarTarjetas(clientId) {
  const { data } = await withRetries(() =>
    axios.post(`${STRIPE_PROXY_URL}/payment_method/list`, { client_id: clientId })
  );
  return Array.isArray(data && data.data) ? data.data : [];
}

export function predeterminarTarjeta(clientId, paymentMethodId) {
  return axios.post(`${STRIPE_PROXY_URL}/payment_method/default`, {
    client_id: clientId,
    payment_method_id: paymentMethodId,
  });
}

export function eliminarTarjeta(clientId, paymentMethodId) {
  return axios.post(`${STRIPE_PROXY_URL}/payment_method/delete`, {
    payment_method_id: paymentMethodId,
    client_id: clientId,
  });
}

/** Suscripciones vigentes del cliente. */
export async function suscripcionesDe(clientId) {
  const { data } = await withRetries(() =>
    axios.get(`${STRIPE_PROXY_URL}/subscription/${clientId}`)
  );
  return Array.isArray(data && data.active_subscription) ? data.active_subscription : [];
}

export async function crearSuscripcion(clientId) {
  const { data } = await axios.post(`${STRIPE_PROXY_URL}/subscription`, {
    customerId: clientId,
    trial_period_days: 0,
  });
  return data;
}

/**
 * Cancela una suscripción.
 *
 * La respuesta es la única fuente que habla de *esta* suscripción: releer
 * `/subscription/{cliente}` no sirve para confirmar, porque responde por el
 * cliente completo y puede tener otras activas.
 */
export async function cancelarSuscripcion(subscriptionId) {
  const { data } = await axios.post(`${STRIPE_PROXY_URL}/subscription/cancel`, {
    subscription_id: subscriptionId,
  });

  const cancelada = (data && data.status === "canceled") || !!(data && data.canceled_at);
  if (!cancelada) {
    throw new Error("Stripe no confirmó la cancelación.");
  }
  return data;
}

/** Descripción corta de una tarjeta: "Visa •••• 4242". */
export function describirTarjeta(metodo) {
  const card = (metodo && metodo.card) || {};
  const marca = card.brand ? card.brand.charAt(0).toUpperCase() + card.brand.slice(1) : "Tarjeta";
  return card.last4 ? `${marca} •••• ${card.last4}` : marca;
}

/** Vencimiento de una tarjeta como "MM/AAAA". */
export function vencimientoDeTarjeta(metodo) {
  const card = (metodo && metodo.card) || {};
  if (!card.exp_month || !card.exp_year) return "";
  return `${String(card.exp_month).padStart(2, "0")}/${card.exp_year}`;
}
